import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDM } from '../../context/DMContext';
import UserAvatar from './UserAvatar';

const DMSelectorModal = ({ isOpen, onClose, onSelectUser, onConversationCreated }) => {
  const {
    userSuggestions,
    searchResults,
    searchUsers,
    conversations
  } = useDM();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('suggestions');
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const searchInputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchUsers(searchQuery);
        setActiveTab('search');
      } else if (searchQuery.trim().length === 0) {
        setActiveTab('suggestions');
      }
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery, searchUsers]);

  // Handle user selection
  const handleUserSelect = useCallback(async (user) => {
    if (isCreatingConversation) return;

    try {
      setIsCreatingConversation(true);

      // Check if conversation already exists
      const existingConversation = conversations.find(conv => 
        conv.other_user_id === user.user_id || conv.other_user_id === user.id
      );

      if (existingConversation) {
        // Open existing conversation
        onConversationCreated(existingConversation.conversation_id, {
          id: existingConversation.other_user_id,
          username: existingConversation.other_username,
          display_name: existingConversation.other_display_name,
          avatar_url: existingConversation.other_avatar_url
        });
      } else {
        // Don't create conversation yet - just pass user info
        // Conversation will be created when first message is sent
        onConversationCreated(null, {
          id: user.user_id || user.id,
          username: user.username,
          display_name: user.display_name,
          avatar_url: user.avatar_url
        });
      }

      onSelectUser(user);
      onClose();
    } catch (error) {
      console.error('Failed to select user:', error);
    } finally {
      setIsCreatingConversation(false);
    }
  }, [isCreatingConversation, conversations, onConversationCreated, onSelectUser, onClose]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setSearchQuery('');
    setActiveTab('suggestions');
    onClose();
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const UserListItem = ({ user, context }) => (
    <button
      onClick={() => handleUserSelect(user)}
      disabled={isCreatingConversation}
      className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
    >
      <UserAvatar
        user={{
          id: user.user_id || user.id,
          avatar_url: user.avatar_url,
          username: user.username
        }}
        size="w-10 h-10"
        textSize="text-sm"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {user.display_name || user.username}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          @{user.username}
        </div>
        {context && (
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {context}
          </div>
        )}
      </div>
      <div className="text-gray-400 dark:text-gray-500">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );

  const SuggestionCategory = ({ title, users, icon }) => {
    if (!users || users.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 px-4">
          {icon}
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">({users.length})</span>
        </div>
        <div className="space-y-1">
          {users.slice(0, 5).map((user) => (
            <UserListItem
              key={user.user_id || user.id}
              user={user}
              context={user.context_info}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div 
  className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center px-4"
  onClick={handleBackdropClick}
>
      <div className="w-full max-w-md bg-zinc-800 rounded-lg shadow-2xl overflow-hidden flex flex-col" style={{ height: 'min(90vh, calc(100dvh - 80px))', maxHeight: 'calc(100dvh - env(safe-area-inset-top, 20px) - env(safe-area-inset-bottom, 20px) - 40px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Start a Conversation
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-zinc-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username or name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'search' && searchQuery ? (
            /* Search Results */
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Search Results
              </h3>
              {searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((user) => (
                    <UserListItem
                      key={user.id}
                      user={user}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-sm">No users found</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              )}
            </div>
          ) : (
            /* Suggestions */
            <div className="py-4">
              <SuggestionCategory
                title="Your Teams"
                users={userSuggestions.teamMembers}
                icon={
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                }
              />

              <SuggestionCategory
                title="Tournament Players"
                users={userSuggestions.tournamentParticipants}
                icon={
                  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                }
              />

              <SuggestionCategory
                title="Recent Users"
                users={userSuggestions.recentUsers}
                icon={
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />

              {/* Empty state */}
              {(!userSuggestions.teamMembers?.length && 
                !userSuggestions.tournamentParticipants?.length && 
                !userSuggestions.recentUsers?.length) && (
                <div className="text-center py-12 px-4">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Start chatting with other users
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Search for users above to start a conversation
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Join teams or tournaments to see suggested users here
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-700 p-4">
          <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
            <span>Start messaging with anyone on the platform</span>
            <button
              onClick={handleClose}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Loading overlay */}
        {isCreatingConversation && (
          <div className="absolute inset-0 bg-zinc-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#31C590] mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Starting conversation...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DMSelectorModal;